import React from "react";
import PropTypes from "prop-types";
import QueueAnim from "rc-queue-anim";
import CircularProgress from "@mui/material/CircularProgress";
import APPCONFIG from "constants/Config";
import {
  consumeLoginLink,
  readBindingSecret
} from "services/loginLinkService";
import { renderHeader } from "routes/cashbox-approval/CashboxApproval";
import LinkExpired from "routes/cashbox-approval/LinkExpired";
import { completeLoginLink } from "./completeLoginLink";
import {
  PLATFORM_ANDROID,
  PLATFORM_IOS,
  mobilePlatform,
  isWebOnly,
  openAndroidApp,
  openIOSApp
} from "./appHandoff";

const PHASE_LOADING = "loading";
const PHASE_CODE = "code";
const PHASE_ERROR = "error";
const PHASE_EXPIRED = "expired";

export const LOGIN_LINK_EXPIRED_TEXT = "This login link is no longer valid.";

const appHandoffPlatform = isCustomerApp => {
  if (!isCustomerApp) {
    return null;
  }
  const platform = mobilePlatform();
  if (platform === PLATFORM_IOS) {
    return PLATFORM_IOS;
  }
  if (platform === PLATFORM_ANDROID && !isWebOnly()) {
    return PLATFORM_ANDROID;
  }
  return null;
};

export const renderLoginLinkExpired = onBackToLogin => (
  <div className="container-fluid page-dashboard" style={{ height: "auto" }}>
    <QueueAnim type="bottom" className="ui-animate">
      <div key="1">
        <article className="article">
          {renderHeader()}
          <LinkExpired
            text={LOGIN_LINK_EXPIRED_TEXT}
            onBackToLogin={onBackToLogin}
          />
        </article>
      </div>
    </QueueAnim>
  </div>
);

export const renderLoginLinkCard = (error, content) => (
  <div className="page-login login-screen">
    <div className="main-body">
      <QueueAnim type="bottom" className="ui-animate">
        <div key="1">
          <div className="body-inner">
            <div className="card bg-white">
              <div className="card-content">
                <section className="logo text-center">
                  <h1>
                    <a href="https://wakilni.com/">
                      <img
                        src="/assets/images/wakilni-logo-v2.png"
                        height="28"
                        alt={APPCONFIG.brand}
                      />
                    </a>
                  </h1>
                  <h3>Log In</h3>
                  {error && (
                    <div className="login-response-error" role="alert">
                      {error}
                    </div>
                  )}
                </section>
                {content}
              </div>
            </div>
          </div>
        </div>
      </QueueAnim>
    </div>
  </div>
);

class LoginLink extends React.Component {
  constructor(props, context) {
    super(props, context);

    this._isMounted = false;
    this._handoffPlatform = appHandoffPlatform(props.isCustomerApp);
    this._bindingSecret = this._handoffPlatform ? null : readBindingSecret();

    this.state = {
      token: props.match.params.token,
      phase: PHASE_LOADING,
      code: null,
      error: null
    };
  }

  componentDidMount() {
    this._isMounted = true;
    window.history.replaceState(window.history.state, "", "/login");

    this.consume();

    if (this._handoffPlatform === PLATFORM_ANDROID) {
      openAndroidApp(this.state.token);
    } else if (this._handoffPlatform === PLATFORM_IOS) {
      openIOSApp(this.state.token);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  consume = () => {
    this.setState({ phase: PHASE_LOADING, error: null });

    this.props
      .dispatch(
        consumeLoginLink({
          token: this.state.token,
          ...(this._bindingSecret
            ? { binding_secret: this._bindingSecret }
            : {})
        })
      )
      .then(
        data => {
          if (!this._isMounted) {
            return;
          }

          if (data && data.access_token) {
            completeLoginLink(this.props.dispatch, data);
            return;
          }

          this.setState({ phase: PHASE_CODE, code: data.login_code });
        },
        ({ statusCode, error }) => {
          if (!this._isMounted) {
            return;
          }

          if (statusCode === 422) {
            this.setState({ phase: PHASE_EXPIRED });
            return;
          }

          this.setState({ phase: PHASE_ERROR, error });
        }
      );
  };

  handleOpenApp = () => {
    openIOSApp(this.state.token);
  };

  renderOpenAppButton() {
    if (this._handoffPlatform !== PLATFORM_IOS) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="login-divider">
          <span>or</span>
        </div>
        <button
          type="button"
          className="login-btn login-btn-outline"
          onClick={this.handleOpenApp}
        >
          <span className="login-btn-label">
            <span className="material-icons">open_in_new</span>
            Open in the app
          </span>
        </button>
      </React.Fragment>
    );
  }

  renderContent() {
    const { phase, code } = this.state;

    if (phase === PHASE_LOADING) {
      return (
        <div className="login-link-loader">
          <CircularProgress size={32} color="inherit" />
        </div>
      );
    }

    if (phase === PHASE_ERROR) {
      return (
        <div className="login-form">
          <button
            type="button"
            className="login-btn login-btn-primary"
            onClick={this.consume}
          >
            <span className="login-btn-label">Try again</span>
          </button>
          {this.renderOpenAppButton()}
        </div>
      );
    }

    return (
      <div className="login-form">
        <div className="login-link-code" aria-label="6-digit code">
          {code}
        </div>
        <div className="login-link-hint text-center">
          Enter this code where you asked for the login link. Never share it
          with anyone.
        </div>
        {this.renderOpenAppButton()}
      </div>
    );
  }

  render() {
    const { phase, error } = this.state;

    if (phase === PHASE_EXPIRED) {
      return renderLoginLinkExpired(null);
    }

    return renderLoginLinkCard(
      phase === PHASE_ERROR ? error : null,
      this.renderContent()
    );
  }
}

LoginLink.propTypes = {
  match: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  isCustomerApp: PropTypes.bool
};

LoginLink.defaultProps = {
  isCustomerApp: false
};

export default LoginLink;
